import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import {
  isSidebarShortcut,
  nextCollapsed,
  readCollapsed,
  writeCollapsed,
  type StorageLike,
} from '@/lib/sidebar-state';

export interface SidebarState {
  /** Desktop (md+): icon rail instead of the expanded sidebar. Persisted. */
  collapsed: boolean;
  /** Phone (< md): nav drawer open. Memory-only. */
  drawerOpen: boolean;
  toggleCollapsed(): void;
  openDrawer(): void;
  closeDrawer(): void;
}

const SidebarStateContext = createContext<SidebarState | null>(null);

function browserStorage(): StorageLike | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function SidebarStateProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(browserStorage()));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = useCallback(() => setCollapsed(nextCollapsed), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    writeCollapsed(browserStorage(), collapsed);
  }, [collapsed]);

  // A nav tap inside the drawer changes the route: close it behind the user.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isSidebarShortcut(e)) return;
      e.preventDefault();
      toggleCollapsed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleCollapsed]);

  const value = useMemo(
    () => ({ collapsed, drawerOpen, toggleCollapsed, openDrawer, closeDrawer }),
    [collapsed, drawerOpen, toggleCollapsed, openDrawer, closeDrawer],
  );
  return <SidebarStateContext.Provider value={value}>{children}</SidebarStateContext.Provider>;
}

export function useSidebarState(): SidebarState {
  const state = useContext(SidebarStateContext);
  if (!state) throw new Error('useSidebarState must be used inside <SidebarStateProvider>');
  return state;
}
